import React, { useEffect, useState } from 'react';
import { adminAPI } from '../services/api';
import { RefreshCw, Wifi, WifiOff } from 'lucide-react';

const BOX_COLORS = {
  empty: { bg: '#0f1117', border: '#252733', text: '#8a8da4' },
  occupied: { bg: 'rgba(77,144,254,0.08)', border: 'rgba(77,144,254,0.3)', text: '#4d90fe' },
  door_open: { bg: 'rgba(255,176,32,0.08)', border: 'rgba(255,176,32,0.3)', text: '#ffb020' },
  disabled: { bg: '#0a0b0e', border: '#1a1b25', text: '#3d3f52' },
};

export default function LiveLockers() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');
  const load = async (force = false) => {
    if (!force && document.hidden) return;   // don't poll from a background tab
    try { const r = await adminAPI.liveLockers(); setData(r.data); setError(''); }
    catch (e) { setError('Could not refresh — showing the last known state.'); }
    setLoading(false);
  };
  useEffect(() => {
    load(true);
    const interval = setInterval(load, 20000); // Refresh every 20s
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getBoxStyle = (box) => {
    if (!box.is_enabled) return BOX_COLORS.disabled;
    if (box.is_door_open) return BOX_COLORS.door_open;
    if (box.is_occupied) return BOX_COLORS.occupied;
    return BOX_COLORS.empty;
  };

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Live Locker Status</h1>
        <div style={{display:'flex',gap:16,alignItems:'center'}}>
          <div style={s.legend}>
            <span style={s.legendItem}><span style={{...s.legendDot,background:'#8a8da4'}}/>Empty</span>
            <span style={s.legendItem}><span style={{...s.legendDot,background:'#4d90fe'}}/>Occupied</span>
            <span style={s.legendItem}><span style={{...s.legendDot,background:'#ffb020'}}/>Door Open</span>
          </div>
          <button style={s.refreshBtn} onClick={() => load(true)}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>
      {error && <div style={{background:'rgba(255,68,102,0.1)',border:'1px solid rgba(255,68,102,0.3)',borderRadius:8,padding:'10px 14px',color:'#ff4466',fontSize:13,marginBottom:12}}>{error}</div>}
      {loading ? <div style={s.empty}>Loading...</div> :
        data.map(device => (
          <div key={device.device_number} style={s.deviceCard}>
            <div style={s.deviceHeader}>
              <div>
                <div style={s.deviceName}>{device.name}</div>
                <div style={s.deviceInfo}>{device.device_number} · {device.location}</div>
              </div>
              <div style={{display:'flex',gap:16,alignItems:'center'}}>
                <span style={s.stat}>{device.occupied}/{device.total_boxes} occupied</span>
                {device.doors_open > 0 && <span style={{...s.stat,color:'#ffb020'}}>{device.doors_open} door(s) open</span>}
                <span style={device.is_online ? s.online : s.offline}>
                  {device.is_online ? <><Wifi size={12}/> Online</> : <><WifiOff size={12}/> Offline</>}
                </span>
              </div>
            </div>
            <div style={s.boxGrid}>
              {device.boxes.map(box => {
                const colors = getBoxStyle(box);
                return (
                  <div key={box.id} style={{...s.box, background:colors.bg, borderColor:colors.border}}
                    title={box.active_parcel ? `${box.active_parcel.order_no} — ${box.active_parcel.sender} → ${box.active_parcel.recipient}` : `Box ${box.box_name} (${box.box_size})`}>
                    <div style={{...s.boxName, color:colors.text}}>{box.box_name}</div>
                    <div style={s.boxSize}>{box.box_size}</div>
                    {box.is_door_open && <div style={s.boxStatus}>🔓</div>}
                    {box.is_occupied && !box.is_door_open && <div style={s.boxStatus}>📦</div>}
                    {box.active_parcel && (
                      <div style={s.boxParcel} title={box.active_parcel.recipient}>
                        {box.active_parcel.recipient?.substring(0,8)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      }
    </div>
  );
}

const s = {
  header: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 },
  title: { fontSize:22, fontWeight:700, color:'#e4e5ed' },
  refreshBtn: { display:'flex', alignItems:'center', gap:6, padding:'8px 16px', background:'transparent', border:'1px solid #252733', borderRadius:8, color:'#8789a0', fontSize:12, cursor:'pointer' },
  legend: { display:'flex', gap:16, fontSize:12, color:'#8789a0' },
  legendItem: { display:'flex', alignItems:'center', gap:6 },
  legendDot: { width:8, height:8, borderRadius:'50%' },
  deviceCard: { background:'#0f1117', border:'1px solid #252733', borderRadius:14, marginBottom:20, overflow:'hidden' },
  deviceHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderBottom:'1px solid #252733', flexWrap:'wrap', gap:12 },
  deviceName: { fontSize:16, fontWeight:700, color:'#e4e5ed' },
  deviceInfo: { fontSize:12, color:'#565870', marginTop:2 },
  stat: { fontSize:12, color:'#8789a0', fontWeight:500 },
  online: { display:'inline-flex', alignItems:'center', gap:4, padding:'4px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:'rgba(139,198,62,0.1)', color:'#8BC63E' },
  offline: { display:'inline-flex', alignItems:'center', gap:4, padding:'4px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:'rgba(255,68,102,0.1)', color:'#ff4466' },
  boxGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(80px, 1fr))', gap:8, padding:16 },
  box: { border:'1.5px solid', borderRadius:10, padding:10, textAlign:'center', minHeight:80, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', transition:'all 0.2s' },
  boxName: { fontSize:16, fontWeight:800 },
  boxSize: { fontSize:10, color:'#565870', marginTop:2 },
  boxStatus: { fontSize:14, marginTop:4 },
  boxParcel: { fontSize:9, color:'#4d90fe', marginTop:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'100%' },
  empty: { padding:60, textAlign:'center', color:'#565870', fontSize:14 },
};
